import { inject, Injectable } from "@angular/core";
import {
    combineLatest,
    distinctUntilChanged,
    filter,
    map,
    merge,
    Observable,
    scan,
    shareReplay,
    Subject,
    switchMap,
    tap
} from "rxjs";
import { ApiClientService } from "~app/common/services/api-client.service";
import { CommandManager } from "~app/common/services/cqs/commandManager";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { UpdateAvtalKundCommand } from "../commands/avtal/UpdateAvtalKundCommand";
import { UpdateAvtalKundKontaktpersonCommand } from "../commands/avtal/UpdateAvtalKundKontaktpersonCommand";
import { Avtal } from "../models/avtal-models";
import { KundKontaktpersonDto } from "../models/skogsvard/kontaktperson-models";
import { KundListItem, SearchKundResponseDto } from "../models/skogsvard/kund-models";
import { AvtalMediatorService } from "./avtal-mediator.service";

@Injectable()
export class KundMediatorService {
    kund$: Observable<KundListItem>;
    kontaktpersoner$: Observable<KundKontaktpersonDto[]>;
    kontaktperson$: Observable<KundKontaktpersonDto>;

    private readonly commandManager = inject(CommandManager);
    private readonly avtalMediatorService = inject(AvtalMediatorService);
    private readonly apiClientService = inject(ApiClientService);
    private readonly addKundSrc = new Subject<SearchKundResponseDto>();
    private readonly updateKontaktpersonSrc = new Subject<number>();

    private readonly avtal$: Observable<Avtal> = this.avtalMediatorService.avtal$.pipe(
        filter(avtal => ObjectUtils.hasValue(avtal)),
        map(avtal => avtal.current),
        distinctUntilChanged((a,b) => a.guid === b.guid)
    );

    private readonly fetchKund$ = this.avtal$.pipe(
        filter(avtal => avtal.id > 0),
        switchMap(avtal => this.getKund(avtal.id)),
        filter(kund => !ObjectUtils.isNullOrUndefined(kund)),
        map(kund => (_curr: SearchKundResponseDto) => kund)
    );

    private readonly selectKund$ = combineLatest([
        this.avtal$,
        this.addKundSrc
    ]).pipe(
        tap(([
            avtal,
            kund
        ]) => this.updateKundCommand(avtal, kund)),
        map(([
            _avtal,
            kund
        ]) => kund),
        map(kund => (_curr: SearchKundResponseDto) => kund)
    );

    private readonly updateKontaktperson$ = combineLatest([
        this.avtal$,
        this.updateKontaktpersonSrc
    ]).pipe(
        tap(([
            avtal,
            kontaktpersonId
        ]) => this.updateKontaktpersonCommand(avtal, kontaktpersonId)),
        map(([
            _avtal,
            kontaktpersonId
        ]) => (curr: SearchKundResponseDto) => ({
            ...curr,
            kontaktpersonId
        }))
    );

    constructor() {
        const kund$: Observable<SearchKundResponseDto> = merge(
            this.fetchKund$,
            this.selectKund$,
            this.updateKontaktperson$
        ).pipe(
            scan((acc: SearchKundResponseDto, reducer: (curr: SearchKundResponseDto) => SearchKundResponseDto) => reducer(acc), {}),
            filter((kund) => kund.id > 0),
            shareReplay(1)
        );

        this.kund$ = kund$.pipe(
            map(dto => new KundListItem(dto))
        );

        this.kontaktpersoner$ = kund$.pipe(
            distinctUntilChanged((a,b) => a.id === b.id),
            switchMap(kund => this.getKontaktpersoner(kund.id)),
            shareReplay(1)
        );

        this.kontaktperson$ = combineLatest([
            this.kontaktpersoner$,
            kund$
        ]).pipe(
            filter(([
                _kontaktpersoner,
                kund
            ]) => kund.kontaktpersonId > 0),
            map(([
                kontaktpersoner,
                kund
            ]) => kontaktpersoner.find(k => k.id === kund.kontaktpersonId)),
            shareReplay(1)
        );
    }

    addKund(kund: KundListItem) {
        this.addKundSrc.next(kund.model);
    }

    updateKontaktperson(kontaktpersonId: number) {
        this.updateKontaktpersonSrc.next(kontaktpersonId);
    }

    private getKund(avtalId: number): Observable<SearchKundResponseDto> {
        return this.apiClientService.get<SearchKundResponseDto>(`virkeskop2/avtal/${avtalId}/kund`);
    }

    private getKontaktpersoner(kundId: number): Observable<KundKontaktpersonDto[]> {
        return this.apiClientService.get<KundKontaktpersonDto[]>(`virkeskop/kund/${kundId}/kontaktpersoner`);
    }

    private updateKontaktpersonCommand(avtal: Avtal, kontaktpersonId: number) {
        this.commandManager.updateCommand(new UpdateAvtalKundKontaktpersonCommand(avtal.guid, kontaktpersonId));
        this.avtalMediatorService.invalidateAvtal();
    }

    private updateKundCommand(avtal: Avtal, kund: SearchKundResponseDto) {
        this.commandManager.updateCommand(new UpdateAvtalKundCommand(avtal.guid, kund.id));
        this.avtalMediatorService.invalidateAvtal();
    }
}
